import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap } from "lucide-react";
import { API } from "@/config/api";
import { useAuth } from "@/config/AuthContext";

const Signup_v2 = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/signup`, 
        { name, email, password },
        { withCredentials: true }
      );
      
      // Get user data and update auth context
      const userResponse = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      login(userResponse.data);
      
      toast.success('Account created successfully!');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await axios.get(`${API}/auth/google-login`, {
        headers: { origin: window.location.origin }
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate Google login');
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <Zap className="h-10 w-10 text-yellow-400" />
            <span className="text-3xl font-heading font-bold text-white">DueDay</span>
          </div>

          <h1 className="text-4xl font-heading font-bold mb-2 text-white">Join DueDay</h1>
          <p className="text-slate-400 mb-8">Create your safety net in minutes</p>

          <Button
            data-testid="google-signup-btn"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full rounded-full py-6 bg-slate-800 border-slate-700 text-white hover:bg-slate-700 mb-8"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-slate-900 text-slate-400">Or sign up with email</span>
            </div>
          </div>

          <form onSubmit={handleSignup} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-slate-300" data-testid="name-label">Full Name</Label>
              <Input
                id="name"
                data-testid="signup-name-input"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-yellow-400"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-300" data-testid="email-label">Email</Label>
              <Input
                id="email"
                data-testid="signup-email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-yellow-400"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300" data-testid="password-label">Password</Label>
              <Input
                id="password"
                data-testid="signup-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-yellow-400"
                required
                minLength={6}
              />
            </div>

            <Button
              data-testid="signup-submit-btn"
              type="submit"
              className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full px-8 py-6 text-lg font-bold w-full"
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-slate-400 mt-8">
            Already have an account?{' '}
            <button
              data-testid="goto-login-btn"
              onClick={() => navigate('/login')}
              className="text-yellow-400 font-medium hover:underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-orange-500/20"></div>
        <img
          src="https://images.pexels.com/photos/8490186/pexels-photo-8490186.jpeg"
          alt="Calm living room environment"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};

export default Signup_v2;